import { attachStateListener, checkToolPermission, applySidebarVisualState, getSingleAllowedAdminView } from "./utils/sidebarManager.js";
import { applySheets } from "../../../app/utils/styleCache.js";
import { openDialog } from "../../features/app-dialog/utils/core/dialogManager.js";
import { setView } from "../../../app/viewManager.js";
import { hasPermission } from "../../../app/auth.js";

export class AppSidebar extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: "open" });
        this.isMounted = false;
        this.unsubscribe = null;
        this._closePopup = () => this.shadowRoot.getElementById('adminPopup')?.classList.remove('open');
    }

    connectedCallback() {
        this.render();
        this.isMounted = true;
        this.setupEventListeners();
        applySidebarVisualState(this);
        document.addEventListener('click', this._closePopup, true);

        applySheets(this.shadowRoot, [
            "components/layout/app-sidebar/app-sidebar.css",
            "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css",
        ]).then(() => requestAnimationFrame(() => {
            this.unsubscribe = attachStateListener(this);
            this.shadowRoot.getElementById('sidebar')?.classList.add('loaded');
            this.setAttribute('loaded', '');
        }));
    }

    disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        document.removeEventListener('click', this._closePopup, true);
    }

    setupEventListeners() {
        const shadow = this.shadowRoot;

        shadow.getElementById('toolCreate')?.addEventListener('click', () => { if (checkToolPermission('Create')) openDialog('create') });
        shadow.getElementById('toolExport')?.addEventListener('click', () => { if (checkToolPermission('Export')) openDialog('export') });

        shadow.getElementById('toolTime')?.addEventListener('click',     () => setView('Time'));
        shadow.getElementById('toolTimelogs')?.addEventListener('click', () => setView('Timelogs'));
        shadow.getElementById('toolEntries')?.addEventListener('click',  () => setView('Entries'));

        shadow.getElementById('toolCustomers')?.addEventListener('click', () => setView('Customers'));
        shadow.getElementById('toolUsers')?.addEventListener('click',     () => setView('Users'));

        shadow.getElementById('toolAdmin')?.addEventListener('click', event => {
            event.stopPropagation();
            const singleView = getSingleAllowedAdminView();
            if (singleView) {
                setView(singleView.charAt(0).toUpperCase() + singleView.slice(1));
                return;
            }
            const popup = shadow.getElementById('adminPopup');
            const rect  = shadow.getElementById('toolAdmin').getBoundingClientRect();
            popup.style.top = `${rect.top}px`;
            popup.classList.toggle('open');
            applySidebarVisualState(this);
        });

        ['Projects', 'Teams', 'Roles', 'Applications', 'Services'].forEach(view => {
            shadow.getElementById(`popup${view}`)?.addEventListener('click', () => {
                shadow.getElementById('adminPopup').classList.remove('open');
                setView(view);
            });
        });

        shadow.getElementById('toolSettings')?.addEventListener('click', () => openDialog('settings'));
    }

    render() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 62px;
                    height: calc(100vh - 80px);
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }
                :host([loaded]) { opacity: 1 }
                .toolContainer, .separator, .toolSettings {
                    opacity: 0;
                    transition: opacity 0.4s ease;
                }
                .sidebar.loaded .toolContainer,
                .sidebar.loaded .separator,
                .sidebar.loaded .toolSettings { opacity: 1 }
            </style>
            <aside class="sidebar" id="sidebar">
                <div class="tool-cntainer">
                    <div class="tool create" id="toolCreate" data-tooltip="Erstellen"><i class="fa-solid fa-plus fa-xl"></i></div>
                    <div class="tool export" id="toolExport" data-tooltip="Exportieren"><i class="fa-solid fa-file-arrow-down fa-lg"></i></div>
                </div>

                <div class="separator"></div>

                <div class="tool-container">
                    <div class="tool default view" id="toolTime"     data-tooltip="Zeiterfassung"><i class="fa-solid fa-stopwatch fa-lg"></i></div>
                    <div class="tool default view" id="toolTimelogs" data-tooltip="Arbeitstage"><i class="fa-solid fa-calendar-days fa-lg"></i></div>
                    <div class="tool default view" id="toolEntries"  data-tooltip="Leistungsnachweise"><i class="fa-solid fa-clipboard-check fa-lg"></i></div>
                </div>

                <div class="separator" id="adminSectionSeparator"></div>

                <div class="tool-container" id="adminSection">
                    <div class="tool default view" id="toolCustomers" data-tooltip="Kundenverwaltung"><i class="fa-solid fa-building fa-lg"></i></div>
                    <div class="tool default view" id="toolUsers"     data-tooltip="Benutzerverwaltung"><i class="fa-solid fa-users fa-lg"></i></div>

                </div>

                <div class="separator"></div>

                <div class="tool-container">
                    <div class="tool default view" id="toolAdmin"     data-tooltip="Administration"><i class="fa-solid fa-screwdriver-wrench fa-lg"></i></div>
                </div>

                <div class="tool-container last-container">
                    <div class="tool settings" id="toolSettings" data-tooltip="Einstellungen"><i class="fa-solid fa-gear fa-lg"></i></div>
                </div>

                <div class="admin-popup" id="adminPopup">
                    <div class="popup-item" id="popupTeams"><i class="fa-solid fa-users-gear fa-sm"></i> Teams</div>
                    <div class="popup-item" id="popupRoles"><i class="fa-solid fa-key fa-sm"></i> Rollen</div>
                    <div class="popup-separator" id="popupTeamsRolesSeparator"></div>
                    <div class="popup-item" id="popupProjects"><i class="fa-solid fa-folder-plus fa-sm"></i> Projekte</div>
                    <div class="popup-item" id="popupApplications"><i class="fa-solid fa-window-restore fa-sm"></i> Applikationen</div>
                    <div class="popup-item" id="popupServices"><i class="fa-solid fa-wrench fa-sm"></i> Services</div>
                </div>
            </aside>
        `;
    }
}

if (!customElements.get("app-sidebar")) {
    customElements.define("app-sidebar", AppSidebar);
}